import React, { useMemo } from 'react';
import { GoogleMap, Polyline, Marker } from '@react-google-maps/api';
import { FaRoute, FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

// ============================================
// STORED ROUTE MAP — draws the route as it was QUOTED
// ============================================
// Read-only. It renders the polyline stored in `quote_routes.route_optimized` (captured once,
// at quote time, by InteractiveRouteMap) so a load page can show the EXACT route the client was
// quoted and priced on — it can never drift from the quote.
//
// This is why route capture existed: before it, `route_optimized` was NULL on every row, so
// these pages had nothing to draw and just showed "coming next".
//
// No Directions API call is made here — everything comes from the stored object, so opening a
// load never costs a Google request and never changes what is shown.
// ============================================

// Local decoder: the stored polyline is Google's encoded format and we deliberately do NOT
// depend on the `geometry` library being loaded (useGoogleMaps loads only places + routes).
// Same algorithm as backend/utils/routeGeometry.js — decode only, never re-encode.
const decodePolyline = (encoded) => {
  if (!encoded || typeof encoded !== 'string') return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let b;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

const toLatLng = (p) =>
  p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))
    ? { lat: Number(p.lat), lng: Number(p.lng) }
    : null;

function StoredRouteMap({
  route,
  height = 340,
  emptyMessage = 'The route for this job has not been captured yet.',
  accent = '#00A86B'
}) {
  const { isLoaded } = useGoogleMaps();

  const polyline = route && typeof route.polyline === 'string' ? route.polyline : null;
  const points = useMemo(() => decodePolyline(polyline), [polyline]);
  const path = useMemo(() => points.map((p) => ({ lat: p.lat, lng: p.lng })), [points]);

  const origin = toLatLng(route && route.origin) || path[0] || null;
  const destination = toLatLng(route && route.destination) || path[path.length - 1] || null;
  const stops = ((route && route.stops) || []).map(toLatLng).filter(Boolean);
  const borders = (route && Array.isArray(route.border_posts) ? route.border_posts : []).map(toLatLng).filter(Boolean);
  const center = path.length ? path[Math.floor(path.length / 2)] : { lat: -28.4793, lng: 24.6729 };

  // Nothing captured (or an old quote from before route capture) — say so plainly.
  if (!path.length) {
    return (
      <div className="rounded-lg p-6 text-center" style={{ backgroundColor: '#F8F9FA', border: '1px solid #E8ECEF' }}>
        <FaRoute size={22} className="mx-auto mb-2" style={{ color: '#CBD5E0' }} />
        <p className="text-sm" style={{ color: '#4A5568' }}>{emptyMessage}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="rounded-lg flex items-center justify-center gap-2 text-sm" style={{ height, backgroundColor: '#F8F9FA', border: '1px solid #E8ECEF', color: '#4A5568' }}>
        <FaSpinner className="animate-spin" /> Loading route map…
      </div>
    );
  }

  return (
    <div>
      <div className="rounded-lg overflow-hidden" style={{ height, border: '1px solid #E8ECEF' }}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={center}
          zoom={6}
          onLoad={(map) => {
            // Fit the WHOLE quoted route, not just the endpoints.
            const bounds = new window.google.maps.LatLngBounds();
            path.forEach((p) => bounds.extend(p));
            map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
          }}
          options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true, zoomControl: true }}
        >
          <Polyline path={path} options={{ strokeColor: accent, strokeWeight: 4, strokeOpacity: 0.85 }} />
          {origin && <Marker position={origin} title="Pickup" />}
          {stops.map((s, i) => <Marker key={`stop-${i}`} position={s} title={`Stop ${i + 1}`} />)}
          {destination && <Marker position={destination} title="Delivery" />}
          {borders.map((b, i) => <Marker key={`border-${i}`} position={b} title="Border post" />)}
        </GoogleMap>
      </div>

      {/* What the route was quoted as — text, so it is readable even if the map is not. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: '#4A5568' }}>
        <span className="flex items-center gap-1">
          <FaMapMarkerAlt size={10} style={{ color: accent }} />
          {route.origin?.address || 'Pickup'} → {route.destination?.address || 'Delivery'}
        </span>
        {route.distance_km ? <span>📏 {route.distance_km} km</span> : null}
        {route.duration_text ? <span>⏱ {route.duration_text}</span> : null}
        {route.captured_at ? <span>Quoted route · {String(route.captured_at).slice(0, 10)}</span> : null}
      </div>
    </div>
  );
}

export default StoredRouteMap;
